import { useEffect, useMemo } from 'react'
import { AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { faBellSlash } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useQuickPassNotifications } from '../../_hooks/use-quickpass-notifications'
import { QuickPassNotification } from '../../_types/quickpass-notification'
import {
  Backdrop,
  Panel,
  Header,
  HeaderText,
  Title,
  Subtitle,
  HeaderActions,
  TextButton,
  CloseButton,
  ScrollArea,
  SectionLabel,
  SectionList,
  Row,
  RowAccent,
  RowGlyph,
  RowBody,
  RowHeader,
  RowTitle,
  RowTime,
  RowText,
  EmptyBlock,
  EmptyEmoji,
  EmptyTitle,
  EmptyBody,
} from './styled'
import {
  buildHeaderSubtitle,
  drawerBackdropMotion,
  drawerPanelMotion,
  formatRelativeTime,
  getNotificationAccentTone,
  getNotificationGlyph,
  partitionByReadState,
} from './helpers'
import { NotificationsDrawerProps } from './types'

/**
 * Slide-in panel listing the renter's QuickPass notifications. Read /
 * unread split into headed sections; row click navigates to `linkPath`,
 * marks read, and closes the drawer. ESC and backdrop click also close.
 *
 * @param {NotificationsDrawerProps} props
 * @returns {JSX.Element | null}
 */
const NotificationsDrawer = ({
  isOpen,
  onClose,
}: NotificationsDrawerProps): JSX.Element | null => {
  const { notifications, unreadCount, markRead, markAllRead } =
    useQuickPassNotifications()
  const navigate = useNavigate()

  useEffect(() => {
    if (isOpen === false) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen === true) {
      const previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = previousOverflow
      }
    }

    return undefined
  }, [isOpen])

  const buckets = useMemo(
    () => partitionByReadState(notifications),
    [notifications],
  )

  const stopPropagation = (event: { stopPropagation: () => void }): void => {
    event.stopPropagation()
  }

  const handleRowClick = async (
    entry: QuickPassNotification,
  ): Promise<void> => {
    if (entry.isRead === false) {
      await markRead(entry.id)
    }

    onClose()

    if (entry.linkPath !== '') {
      navigate(entry.linkPath)
    }
  }

  const handleMarkAllRead = async (): Promise<void> => {
    if (unreadCount === 0) {
      return
    }

    await markAllRead()
  }

  if (isOpen === false) {
    return null
  }

  const subtitleText = buildHeaderSubtitle(unreadCount)
  const isEmpty = notifications.length === 0

  return (
    <AnimatePresence>
      <Backdrop
        variants={drawerBackdropMotion}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onClose}
      >
        <Panel
          variants={drawerPanelMotion}
          initial="hidden"
          animate={{
            ...drawerPanelMotion.visible,
            transition: {
              type: 'tween',
              duration: 0.32,
              ease: [0.32, 0.72, 0, 1],
            },
          }}
          exit="hidden"
          onClick={stopPropagation}
        >
          <Header>
            <HeaderText>
              <Title>Notifications</Title>
              <Subtitle>{subtitleText}</Subtitle>
            </HeaderText>
            <HeaderActions>
              <TextButton
                type="button"
                onClick={handleMarkAllRead}
                disabled={unreadCount === 0}
              >
                Mark all read
              </TextButton>
              <CloseButton
                type="button"
                onClick={onClose}
                aria-label="Close notifications"
              >
                ×
              </CloseButton>
            </HeaderActions>
          </Header>

          {isEmpty === true ? (
            <EmptyBlock>
              <EmptyEmoji aria-hidden="true">
                <Icon icon={faBellSlash} label="Bell" />
              </EmptyEmoji>
              <EmptyTitle>Nothing here yet</EmptyTitle>
              <EmptyBody>
                As soon as a place you saved drops in price, comes back online,
                or anything else interesting happens — you&apos;ll see it here
                first.
              </EmptyBody>
            </EmptyBlock>
          ) : (
            <ScrollArea>
              {buckets.unread.length > 0 ? (
                <div>
                  <SectionLabel>Unread</SectionLabel>
                  <SectionList>
                    {buckets.unread.map(entry => (
                      <Row
                        key={entry.id}
                        isUnread={true}
                        onClick={() => handleRowClick(entry)}
                      >
                        <RowAccent
                          tone={getNotificationAccentTone(entry.type)}
                        />
                        <RowGlyph aria-hidden="true">
                          <Icon icon={getNotificationGlyph(entry.type)} />
                        </RowGlyph>
                        <RowBody>
                          <RowHeader>
                            <RowTitle isUnread={true}>{entry.title}</RowTitle>
                            <RowTime>
                              {formatRelativeTime(entry.createdAt)}
                            </RowTime>
                          </RowHeader>
                          <RowText>{entry.body}</RowText>
                        </RowBody>
                      </Row>
                    ))}
                  </SectionList>
                </div>
              ) : null}

              {buckets.read.length > 0 ? (
                <div>
                  <SectionLabel>Earlier</SectionLabel>
                  <SectionList>
                    {buckets.read.map(entry => (
                      <Row
                        key={entry.id}
                        isUnread={false}
                        onClick={() => handleRowClick(entry)}
                      >
                        <RowGlyph aria-hidden="true">
                          <Icon icon={getNotificationGlyph(entry.type)} />
                        </RowGlyph>
                        <RowBody>
                          <RowHeader>
                            <RowTitle isUnread={false}>{entry.title}</RowTitle>
                            <RowTime>
                              {formatRelativeTime(entry.createdAt)}
                            </RowTime>
                          </RowHeader>
                          <RowText>{entry.body}</RowText>
                        </RowBody>
                      </Row>
                    ))}
                  </SectionList>
                </div>
              ) : null}
            </ScrollArea>
          )}
        </Panel>
      </Backdrop>
    </AnimatePresence>
  )
}

export default NotificationsDrawer
